/**
 * FoundersCapIcon
 *
 * Custom inline SVG of a vintage international football cap — the kind
 * traditionally awarded for an international appearance. Designed in gold
 * tones for the "Founders' Cap" lifetime tier, but the gradient stops use
 * `data-cap-variant` so a future Trophy Room can render alternate variants
 * (e.g. silver / bronze / annual caps) just by swapping CSS variables.
 *
 * Usage:
 *   <FoundersCapIcon className="w-10 h-10" />
 */
export const FoundersCapIcon = ({ className = '', style, variant = 'gold', ...rest }) => {
  // Variant palettes. Add silver/bronze/etc. here when caps are awarded
  // for milestones in the gamification pack.
  const palettes = {
    gold: {
      domeTop: '#fde047',
      domeMid: '#d97706',
      domeBot: '#78350f',
      trim:    '#fbbf24',
      trimHi:  '#fef3c7',
      peak:    '#451a03',
      peakHi:  '#92400e',
      button:  '#fbbf24',
      cord:    '#fde68a',
      crest:   '#fef3c7',
      seam:    'rgba(69, 26, 3, 0.45)',
    },
  };
  const p = palettes[variant] || palettes.gold;
  // Unique ID suffix so multiple icons on the same page don't collide on defs.
  const uid = Math.random().toString(36).slice(2, 8);

  return (
    <svg
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
      {...rest}
    >
      <defs>
        <linearGradient id={`cap-velvet-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={p.domeTop} />
          <stop offset="55%"  stopColor={p.domeMid} />
          <stop offset="100%" stopColor={p.domeBot} />
        </linearGradient>
        <linearGradient id={`cap-trim-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={p.trimHi} />
          <stop offset="50%"  stopColor={p.trim} />
          <stop offset="100%" stopColor={p.domeBot} />
        </linearGradient>
      </defs>

      {/* Tassel cord arcing down from the button to the side */}
      <path
        d="M 32 11 C 42 12, 50 18, 52 26"
        stroke={p.cord}
        strokeWidth="1.2"
        fill="none"
        strokeLinecap="round"
      />

      {/* Tassel knot + fringe strands */}
      <g transform="translate(50, 26)">
        <circle cx="2" cy="0" r="2.2" fill={p.trim} stroke={p.domeBot} strokeWidth="0.5" />
        <path
          d="M 0 2 L -1 7 M 2 2 L 2 8 M 4 2 L 5 7 M -2 2 L -3 6 M 6 2 L 7 6.5"
          stroke={p.cord}
          strokeWidth="0.9"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      {/* Cap dome — velvet crown */}
      <path
        d="M 13 37 C 13 18, 21 12, 32 12 C 43 12, 51 18, 51 37 Z"
        fill={`url(#cap-velvet-${uid})`}
      />

      {/* Six-panel seams */}
      <g stroke={p.seam} strokeWidth="0.7" fill="none">
        <path d="M 32 12 L 32 36" />
        <path d="M 19 16 Q 23 26, 25 36" />
        <path d="M 45 16 Q 41 26, 39 36" />
        <path d="M 13.5 30 Q 32 23, 50.5 30" />
      </g>

      {/* Trim band along the bottom of the crown */}
      <rect x="11" y="34" width="42" height="3.5" rx="1" fill={`url(#cap-trim-${uid})`} />

      {/* Peak / brim */}
      <path
        d="M 10 37 Q 32 44, 54 37 Q 54 39.5, 50 40.5 L 14 40.5 Q 10 39.5, 10 37 Z"
        fill={p.peakHi}
        stroke={p.peak}
        strokeWidth="0.5"
      />

      {/* Button on top */}
      <circle cx="32" cy="11" r="2.2" fill={p.button} stroke={p.domeBot} strokeWidth="0.5" />

      {/* Front embroidered crest — five-point star inside an oval */}
      <ellipse cx="32" cy="26.5" rx="4.6" ry="5.2" fill={p.peak} opacity="0.55" />
      <path
        d="M 32 23 L 33.05 25.7 L 35.9 26 L 33.7 27.9 L 34.4 30.7 L 32 29.2 L 29.6 30.7 L 30.3 27.9 L 28.1 26 L 30.95 25.7 Z"
        fill={p.crest}
        stroke={p.domeBot}
        strokeWidth="0.35"
      />
    </svg>
  );
};

export default FoundersCapIcon;
